/**
 * Ed25519 device identity management.
 *
 * Each gateway gets its own Ed25519 keypair. The keypair is generated
 * once and stored in localStorage (per-gateway). The device ID is
 * derived as SHA-256 hex of the raw 32-byte public key.
 *
 * Uses @noble/ed25519 for universal browser support (pure JS, ~3KB gzipped).
 */

import { getPublicKeyAsync, signAsync, verifyAsync, utils } from '@noble/ed25519';
import { sha256 } from '@noble/hashes/sha2.js';
import { base64urlEncode, base64urlDecode, bytesToHex } from '../utils/base64url';
import { CLIENT_INFO, CLIENT_SCOPES } from '../utils/constants';
import type { DeviceKeypair } from '../types/gateway';

/**
 * Generate a new Ed25519 keypair and derive the device ID.
 *
 * @returns DeviceKeypair with id, publicKey, and privateKey (all encoded)
 */
export async function generateDeviceKeypair(): Promise<DeviceKeypair> {
  const privateKey = utils.randomSecretKey(); // 32 bytes
  const publicKey = await getPublicKeyAsync(privateKey); // 32 bytes

  // Device ID = SHA-256 hex of raw 32-byte public key
  const hashBytes = sha256(publicKey);
  const deviceId = bytesToHex(hashBytes);

  return {
    id: deviceId,
    publicKey: base64urlEncode(publicKey),
    privateKey: base64urlEncode(privateKey),
  };
}

/**
 * Sign the connect handshake payload with the device's private key.
 *
 * The payload format is:
 * `v2|{deviceId}|{clientId}|{mode}|{role}|{scopes}|{signedAtMs}|{token}|{nonce}`
 *
 * @returns signature (base64url) and signedAt timestamp
 */
export async function signConnectPayload(params: {
  deviceId: string;
  privateKeyB64: string;
  nonce: string;
  token: string;
  signedAt: number;
}): Promise<{ signature: string; signedAt: number }> {
  const { deviceId, privateKeyB64, nonce, token, signedAt } = params;

  // Build the v2 payload string
  const payload = [
    'v2',
    deviceId,
    CLIENT_INFO.id,                  // client.id
    CLIENT_INFO.mode,                // client.mode
    'operator',                      // role
    CLIENT_SCOPES.join(','),          // scopes (comma-separated)
    String(signedAt),                // milliseconds
    token || '',                     // gateway token or device token
    nonce,                           // from connect.challenge
  ].join('|');

  const payloadBytes = new TextEncoder().encode(payload);
  const privKeyBytes = base64urlDecode(privateKeyB64);
  const signature = await signAsync(payloadBytes, privKeyBytes);

  return {
    signature: base64urlEncode(signature),
    signedAt,
  };
}

/**
 * Verify a signature (for testing/debugging).
 */
export async function verifyDeviceSignature(
  message: Uint8Array,
  signature: Uint8Array,
  publicKey: Uint8Array,
): Promise<boolean> {
  return verifyAsync(signature, message, publicKey);
}
